import Logger from "../logger"
import { Comparable } from "../util/Comparable"
import {
  Decision,
  DecisionReason,
  Experiment,
  FeatureFlagDecision,
  HackleUser,
  MatchValueType,
  RemoteConfigDecision
} from "../model/model"
import DecisionProcessor from "./DecisionProcessor"
import { Decisions } from "./Decisions"
import WorkspaceEvaluationFetcher from "../workspace/evaluation/WorkspaceEvaluationFetcher"
import ExperimentRemoteEvaluateRequest from "../evaluation/service/experiment/mode/remote/ExperimentRemoteEvaluateRequest"
import RemoteConfigRemoteEvaluateRequest from "../evaluation/service/remoteconfig/mode/remote/RemoteConfigRemoteEvaluateRequest"
import EvaluateProcessor from "../evaluation/EvaluateProcessor"

const log = Logger.log

/**
 * Decisions from remote evaluation against a server-resolved, per-user `WorkspaceEvaluation`.
 * The experiment/featureFlag/remoteConfig vertical slices are supported; in-app message and bulk
 * enumeration are handled by the expansion phase and degrade for now.
 */
export default class RemoteDecisionProcessor implements DecisionProcessor {
  constructor(
    private readonly workspaceFetcher: WorkspaceEvaluationFetcher,
    private readonly evaluateProcessor: EvaluateProcessor
  ) {}

  experiment(experimentKey: number, user: HackleUser): Decision {
    if (!experimentKey) {
      log.error("experimentKey must not be empty")
      return Decision.of("A", DecisionReason.INVALID_INPUT)
    }

    const workspace = this.workspaceFetcher.workspace(user)

    if (!workspace) {
      log.warn("SDK not ready.")
      return Decision.of("A", DecisionReason.SDK_NOT_READY)
    }

    const experiment = workspace.getExperimentOrNull(experimentKey)

    if (!experiment) {
      log.warn("Experiment does not exist.")
      return Decision.of("A", DecisionReason.EXPERIMENT_NOT_FOUND)
    }

    const request = ExperimentRemoteEvaluateRequest.of(workspace, user, experiment)
    const evaluation = this.evaluateProcessor.experiment(request).evaluation

    return Decisions.experiment(evaluation)
  }

  featureFlag(featureKey: number, user: HackleUser): FeatureFlagDecision {
    if (!featureKey) {
      log.error("featureKey must not be empty")
      return FeatureFlagDecision.off(DecisionReason.INVALID_INPUT)
    }

    const workspace = this.workspaceFetcher.workspace(user)

    if (!workspace) {
      log.warn("SDK not ready.")
      return FeatureFlagDecision.off(DecisionReason.SDK_NOT_READY)
    }

    const featureFlag = workspace.getFeatureFlagOrNull(featureKey)

    if (!featureFlag) {
      log.warn("FeatureFlag does not exist.")
      return FeatureFlagDecision.off(DecisionReason.FEATURE_FLAG_NOT_FOUND)
    }

    const request = ExperimentRemoteEvaluateRequest.of(workspace, user, featureFlag)
    const evaluation = this.evaluateProcessor.experiment(request).evaluation

    return Decisions.featureFlag(evaluation)
  }

  experiments(user: HackleUser): Comparable<Experiment, Decision> {
    const decisions = new Comparable<Experiment, Decision>((a, b) => a.id === b.id)
    const workspace = this.workspaceFetcher.workspace(user)
    if (!workspace) {
      return decisions
    }

    workspace.getExperiments().forEach((experiment) => {
      const request = ExperimentRemoteEvaluateRequest.of(workspace, user, experiment, false)
      const evaluation = this.evaluateProcessor.experiment(request).evaluation
      decisions.add(experiment, Decisions.experiment(evaluation))
    })

    return decisions
  }

  featureFlags(user: HackleUser): Comparable<Experiment, FeatureFlagDecision> {
    const decisions = new Comparable<Experiment, FeatureFlagDecision>((a, b) => a.id === b.id)
    const workspace = this.workspaceFetcher.workspace(user)
    if (!workspace) {
      return decisions
    }

    workspace.getFeatureFlags().forEach((featureFlag) => {
      const request = ExperimentRemoteEvaluateRequest.of(workspace, user, featureFlag, false)
      const evaluation = this.evaluateProcessor.experiment(request).evaluation
      decisions.add(featureFlag, Decisions.featureFlag(evaluation))
    })

    return decisions
  }

  remoteConfig(
    parameterKey: string,
    user: HackleUser,
    requiredType: MatchValueType,
    defaultValue: string | number | boolean
  ): RemoteConfigDecision {
    const workspace = this.workspaceFetcher.workspace(user)

    if (!workspace) {
      log.warn("SDK not ready.")
      return RemoteConfigDecision.of(defaultValue, DecisionReason.SDK_NOT_READY)
    }

    const parameter = workspace.getRemoteConfigParameterOrNull(parameterKey)

    if (!parameter) {
      log.warn("Remote config parameter does not exist.")
      return RemoteConfigDecision.of(defaultValue, DecisionReason.REMOTE_CONFIG_PARAMETER_NOT_FOUND)
    }

    const request = RemoteConfigRemoteEvaluateRequest.of(workspace, user, parameter, requiredType)
    const evaluation = this.evaluateProcessor.remoteConfig(request).evaluation

    return Decisions.remoteConfig(evaluation, requiredType, defaultValue)
  }
}
